import { useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  TrendingUp, Users, Crown, CreditCard, ArrowUpRight,
  Loader2, Zap, Clock, AtSign, Mail, Target,
} from "lucide-react";

export default function AdminGrowthPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/upgrade-intelligence")
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Failed to load growth data"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 size={20} className="animate-spin text-[#888888]" />
      </div>
    );
  }

  const c = data?.conversion || {};
  const s = data?.signals || {};
  const candidates = data?.candidates || [];

  return (
    <ScrollArea className="h-full">
      <div className="p-6 space-y-8 max-w-[1100px]" data-testid="admin-growth-page">
        {/* Header */}
        <div>
          <h2 className="text-xl font-semibold text-[#111111] tracking-tight" data-testid="growth-title">Growth & Monetization</h2>
          <p className="text-sm text-[#888888] mt-0.5">Upgrade candidates, conversion metrics, and revenue signals</p>
        </div>

        {/* Conversion Metrics */}
        <div data-testid="conversion-metrics">
          <h3 className="text-xs font-mono uppercase tracking-widest text-[#888888] mb-3">Conversion Overview</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            <MetricCard icon={Users} label="Total Users" value={c.total_users} color="bg-[#F5F5F5] text-[#888888]" />
            <MetricCard icon={Crown} label="PRO Active" value={c.total_pro} color="bg-blue-50 text-blue-600" />
            <MetricCard icon={TrendingUp} label="Conversion" value={`${c.conversion_rate}%`} sub="free to PRO" color="bg-emerald-50 text-emerald-600" />
            <MetricCard icon={ArrowUpRight} label="Upgrades (7d)" value={c.recent_upgrades_7d} color="bg-violet-50 text-violet-600" />
            <MetricCard icon={ArrowUpRight} label="Upgrades (30d)" value={c.recent_upgrades_30d} color="bg-indigo-50 text-indigo-600" />
          </div>
        </div>

        {/* Plan Breakdown + Revenue Signals */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="plan-breakdown">
          <div className="rounded-lg border border-[#E5E5E5] bg-white p-4">
            <h3 className="text-xs font-mono uppercase tracking-widest text-[#888888] mb-3">Plan Distribution</h3>
            <div className="space-y-2.5">
              <PlanRow label="Monthly" value={c.pro_monthly} total={c.total_pro} color="bg-blue-500" />
              <PlanRow label="Yearly" value={c.pro_yearly} total={c.total_pro} color="bg-violet-500" />
              <PlanRow label="Lifetime" value={c.pro_lifetime} total={c.total_pro} color="bg-emerald-500" />
            </div>
            <div className="flex items-center gap-2 mt-3 pt-3 border-t border-[#E5E5E5]">
              <CreditCard size={12} className="text-[#888888]" />
              <span className="text-xs text-[#888888]">Stripe-paid: <span className="font-medium text-[#555555]">{c.paid_stripe}</span></span>
            </div>
          </div>

          <div className="rounded-lg border border-[#E5E5E5] bg-white p-4">
            <h3 className="text-xs font-mono uppercase tracking-widest text-[#888888] mb-3">Upgrade Signals</h3>
            <div className="space-y-3">
              <SignalRow icon={Zap} label="Active free users (7d)" value={s.active_free_users_7d} hint="engaged but not paying" />
              <SignalRow icon={Target} label="At alias limit" value={s.at_alias_limit} hint="hitting free plan ceiling" />
            </div>
          </div>
        </div>

        {/* Upgrade Candidates */}
        <div data-testid="upgrade-candidates">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs font-mono uppercase tracking-widest text-[#888888]">Top Upgrade Candidates</h3>
            <span className="text-[10px] text-[#888888]">{candidates.length} users ranked by engagement</span>
          </div>

          {candidates.length === 0 ? (
            <div className="rounded-lg border border-[#E5E5E5] bg-white px-6 py-10 text-center">
              <p className="text-sm text-[#888888]">No upgrade candidates found yet</p>
              <p className="text-xs text-[#555555] mt-1">Users need activity before they appear here</p>
            </div>
          ) : (
            <div className="rounded-lg border border-[#E5E5E5] bg-white overflow-hidden">
              <table className="w-full text-sm" data-testid="candidates-table">
                <thead className="bg-[#F5F5F5] border-b border-[#E5E5E5]">
                  <tr>
                    <th className="text-left px-4 py-2.5 text-[10px] font-mono uppercase tracking-widest text-[#888888]">User</th>
                    <th className="text-left px-4 py-2.5 text-[10px] font-mono uppercase tracking-widest text-[#888888]">Aliases</th>
                    <th className="text-left px-4 py-2.5 text-[10px] font-mono uppercase tracking-widest text-[#888888]">Emails</th>
                    <th className="text-left px-4 py-2.5 text-[10px] font-mono uppercase tracking-widest text-[#888888]">Last Seen</th>
                    <th className="text-left px-4 py-2.5 text-[10px] font-mono uppercase tracking-widest text-[#888888]">Score</th>
                    <th className="text-left px-4 py-2.5 text-[10px] font-mono uppercase tracking-widest text-[#888888]">Signals</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E5E5E5]">
                  {candidates.map((u, i) => (
                    <tr key={i} className="hover:bg-[#F7F7F7]" data-testid={`candidate-row-${i}`}>
                      <td className="px-4 py-3">
                        <p className="font-medium text-[#111111] text-sm">{u.name !== "—" ? u.name : u.email}</p>
                        <p className="text-[10px] font-mono text-[#888888]">{u.email}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span className="flex items-center gap-1 text-xs text-[#555555]">
                          <AtSign size={11} className="text-[#888888]" /> {u.aliases}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="flex items-center gap-1 text-xs text-[#555555]">
                          <Mail size={11} className="text-[#888888]" /> {u.emails}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-[#888888]">
                        {u.last_seen ? relTime(u.last_seen) : "—"}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-mono font-semibold tabular-nums ${u.score >= 20 ? "text-emerald-600" : u.score >= 10 ? "text-blue-600" : "text-[#888888]"}`}>
                          {u.score}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {u.signals.map((s) => (
                            <Badge key={s} variant="outline" className={`text-[9px] px-1.5 py-0 ${SIGNAL_COLORS[s] || ""}`}>
                              {SIGNAL_LABELS[s] || s}
                            </Badge>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </ScrollArea>
  );
}

const SIGNAL_COLORS = {
  has_aliases: "border-blue-200 text-blue-600 bg-blue-50",
  active_inbox: "border-emerald-200 text-emerald-600 bg-emerald-50",
  power_user: "border-violet-200 text-violet-600 bg-violet-50",
  recently_active: "border-amber-200 text-amber-600 bg-amber-50",
};

const SIGNAL_LABELS = {
  has_aliases: "Has aliases",
  active_inbox: "Active inbox",
  power_user: "Power user",
  recently_active: "Recent",
};

function MetricCard({ icon: Icon, label, value, sub, color }) {
  return (
    <div className="p-4 rounded-lg border border-[#E5E5E5] bg-white" data-testid={`metric-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className={`w-8 h-8 rounded-md flex items-center justify-center mb-2 ${color}`}>
        <Icon size={15} />
      </div>
      <p className="text-2xl font-semibold text-[#111111] tabular-nums">{value ?? 0}</p>
      <p className="text-[10px] font-mono uppercase tracking-widest text-[#888888] mt-0.5">{label}</p>
      {sub && <p className="text-[10px] text-[#888888] mt-1">{sub}</p>}
    </div>
  );
}

function PlanRow({ label, value, total, color }) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <span className="text-xs text-[#888888]">{label}</span>
        <span className="text-xs font-medium text-[#111111] tabular-nums">{value} <span className="text-[#888888] font-normal">({pct}%)</span></span>
      </div>
      <div className="h-1.5 bg-[#F5F5F5] rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${Math.max(pct, 2)}%` }} />
      </div>
    </div>
  );
}

function SignalRow({ icon: Icon, label, value, hint }) {
  return (
    <div className="flex items-center gap-3">
      <div className="w-8 h-8 rounded-md bg-amber-50 flex items-center justify-center shrink-0">
        <Icon size={14} className="text-amber-600" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between">
          <span className="text-sm text-[#111111]">{label}</span>
          <span className="text-lg font-semibold text-[#111111] tabular-nums">{value}</span>
        </div>
        <p className="text-[10px] text-[#888888]">{hint}</p>
      </div>
    </div>
  );
}

function relTime(val) {
  if (!val) return "—";
  try {
    const d = new Date(val);
    const now = new Date();
    const diffMin = Math.floor((now - d) / 60000);
    if (diffMin < 1) return "just now";
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffH = Math.floor(diffMin / 60);
    if (diffH < 24) return `${diffH}h ago`;
    return `${Math.floor(diffH / 24)}d ago`;
  } catch {
    return String(val);
  }
}
